import { Head } from '@inertiajs/react';
import type { CSSProperties } from 'react';
import { EltStill, type EltShot } from '@/components/elt/elt-still';
import type { LandingsOg } from '@/types';
import { ParrotFiligree } from './components/parrot-filigree';
import { RotatingMate } from './components/rotating-mate';
import { RotatingSkull } from './components/rotating-skull';
import { WireBus } from './components/wire-bus';

// `app/(public)/26-{ar,co,mx}/og/page.tsx` — the 1200×630 card, screenshotted by hand.
// One page for the three stops (PORTING.md G5): Bogotá and Ciudad de México paint it with
// the route group's `theme-ph26` palette, Buenos Aires inverts it and lays a diagonal
// hatch over the card instead of the radial vignette.
const MODEL_CLASS = '-top-6 right-0 bottom-6 left-[-14%]';

const HATCH_STYLE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(135deg, rgba(255, 255, 255, 0.16) 0px, rgba(255, 255, 255, 0.16) 1px, transparent 1px, transparent 12px), repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.08) 0px, rgba(0, 0, 0, 0.08) 1px, transparent 1px, transparent 12px)',
  backgroundPosition: '0 0, 1px 1px',
  opacity: 0.7,
};

const PH26_CARD_STYLE: CSSProperties = {
  width: '1200px',
  height: '630px',
  backgroundImage:
    'radial-gradient(circle at 72% 50%, rgba(0, 0, 0, 0.18) 0%, rgba(0, 0, 0, 0) 65%)',
};

type OgEdition = {
  city: string;
  dates: string;
  rootClass: string;
  cardClass: string;
  cardStyle: CSSProperties;
  hatch: boolean;
  columnClass: string;
  cityClass: string;
  datesClass: string;
  model: 'mate' | 'skull' | 'wire' | 'parrot' | 'telescope';
};

const EDITIONS: Record<string, OgEdition> = {
  '26-ar': {
    city: 'buenos aires',
    dates: '8-10 mayo',
    rootClass: 'flex min-h-screen items-center justify-center bg-primary p-8',
    cardClass: 'relative overflow-hidden border border-background/20 bg-primary',
    cardStyle: { width: '1200px', height: '630px' },
    hatch: true,
    columnClass: 'flex h-full flex-col justify-center pl-20 text-background',
    cityClass: 'mt-8 font-bold font-title text-4xl uppercase tracking-[0.2em]',
    datesClass: 'mt-4 font-title text-3xl uppercase tracking-[0.22em]',
    model: 'mate',
  },
  '26-co': {
    city: 'bogotá',
    dates: '21-23 agosto',
    rootClass:
      'theme-ph26 flex min-h-screen items-center justify-center bg-background p-8',
    cardClass:
      'relative overflow-hidden border border-foreground/20 bg-background',
    cardStyle: PH26_CARD_STYLE,
    hatch: false,
    columnClass: 'flex h-full flex-col justify-center pl-20 text-foreground',
    cityClass: 'mt-8 font-title text-4xl uppercase tracking-[0.2em]',
    datesClass: 'mt-4 font-sans text-3xl uppercase tracking-[0.22em]',
    model: 'wire',
  },
  '26-ve': {
    city: 'caracas',
    dates: '23-25 octubre',
    rootClass:
      'theme-ph26 flex min-h-screen items-center justify-center bg-background p-8',
    cardClass:
      'relative overflow-hidden border border-foreground/20 bg-background',
    cardStyle: PH26_CARD_STYLE,
    hatch: false,
    columnClass: 'flex h-full flex-col justify-center pl-20 text-foreground',
    cityClass: 'mt-8 font-title text-4xl uppercase tracking-[0.2em]',
    datesClass: 'mt-4 font-sans text-3xl uppercase tracking-[0.22em]',
    model: 'parrot',
  },
  '26-cl': {
    city: 'santiago',
    dates: '20-22 noviembre',
    rootClass:
      'theme-ph26 flex min-h-screen items-center justify-center bg-background p-8',
    cardClass:
      'relative overflow-hidden border border-foreground/20 bg-background',
    cardStyle: PH26_CARD_STYLE,
    hatch: false,
    columnClass: 'relative z-20 flex h-full flex-col justify-center pl-20 text-foreground',
    cityClass: 'mt-8 font-title text-4xl uppercase tracking-[0.2em]',
    datesClass: 'mt-4 font-sans text-3xl uppercase tracking-[0.22em]',
    model: 'telescope',
  },
  '26-mx': {
    city: 'ciudad de méxico',
    dates: '19-21 junio',
    rootClass:
      'theme-ph26 flex min-h-screen items-center justify-center bg-background p-8',
    cardClass:
      'relative overflow-hidden border border-foreground/20 bg-background',
    cardStyle: PH26_CARD_STYLE,
    hatch: false,
    columnClass: 'flex h-full flex-col justify-center pl-20 text-foreground',
    cityClass: 'mt-8 font-title text-4xl uppercase tracking-[0.2em]',
    datesClass: 'mt-4 font-sans text-3xl uppercase tracking-[0.22em]',
    model: 'skull',
  },
};

// Santiago's ELT fills the whole card, from an angle picked by hand: high over its left
// shoulder, the slit turned toward you, the four lasers lit. The text column sits over
// the sky on its left, behind a shade.
const SANTIAGO_SHOT: EltShot = {
  camera: { pos: [-102.7, 193.6, 368.9], look: [-88, 40, 0], fov: 33.5 },
  aim: { az: 165, alt: 31 },
  lasers: 1,
  bloom: 0.42,
};

function EditionModel({ model }: { model: OgEdition['model'] }) {
  if (model === 'mate') return <RotatingMate className={MODEL_CLASS} />;
  if (model === 'skull') return <RotatingSkull className={MODEL_CLASS} />;
  if (model === 'parrot') return <ParrotFiligree />;
  if (model === 'telescope') return null; // full bleed, behind the columns
  return <WireBus className={MODEL_CLASS} />;
}

export default function Og({ title, description, editionSlug }: LandingsOg) {
  const edition = EDITIONS[editionSlug];

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
      </Head>
      <div className={edition.rootClass}>
        <div className={edition.cardClass} style={edition.cardStyle}>
          {edition.hatch && (
            <div className="absolute inset-0" style={HATCH_STYLE} />
          )}
          {edition.model === 'telescope' && (
            <>
              <EltStill shot={SANTIAGO_SHOT} className="absolute inset-0" />
              <div className="absolute inset-0 bg-[linear-gradient(90deg,hsl(var(--background))_0%,hsl(var(--background)/0.8)_30%,transparent_58%)]" />
            </>
          )}

          <div className="relative z-10 grid h-full grid-cols-2">
            <div className={edition.columnClass}>
              <div className="whitespace-nowrap font-logo text-[72px] lowercase leading-[0.95] tracking-tight">
                <span className="font-light">platanus hack</span>{' '}
                <span className="font-medium">[26]</span>
              </div>
              <div className={edition.cityClass}>{edition.city}</div>
              <div className={edition.datesClass}>{edition.dates}</div>
            </div>

            <div className="relative h-full">
              <EditionModel model={edition.model} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
